import styled, { css, DefaultTheme } from 'styled-components';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import {
  CheckboxAppearance,
  CheckboxColor,
  CheckboxLabelPosition,
  CheckboxSize,
} from '@/components/UI/Checkbox/Checkbox.types';

function getCheckboxWrapperHeight(size: CheckboxSize) {
  switch (size) {
    case 'sm':
      return 50;
    case 'md':
      return 55;
    case 'lg':
      return 60;
    default:
      return 60;
  }
}

function getRadioColor(color: CheckboxColor, theme: DefaultTheme) {
  switch (color) {
    case 'secondary':
      return theme.palette.system.blue;
    default:
      return theme.palette.brand.main;
  }
}

function getCheckedKnobStyles(
  appearance: CheckboxAppearance,
  color: CheckboxColor
) {
  switch (appearance) {
    case 'switch':
      return css`
        input:checked + ${Knob} {
          background-color: ${({ theme }) => theme.palette.system.green};
        }

        input:checked + ${Knob}:before {
          transform: translateX(20px);
        }
      `;
    case 'filledCheckIcon':
      return css`
        input:checked + ${Knob} {
          border: 1px solid ${({ theme }) => getRadioColor(color, theme)};
        }
        input:checked + ${Knob}:before {
          background-image: url('/images/checkbox-selected.svg');
          opacity: 1;
        }
      `;
    default:
      return css`
        input:checked + ${Knob} {
          border: 1px solid ${({ theme }) => getRadioColor(color, theme)};
        }
        input:checked + ${Knob}:before {
          background-color: ${({ theme }) => getRadioColor(color, theme)};
        }
      `;
  }
}

export const Switch = styled.span<{
  $appearance: CheckboxAppearance;
  $isVisible: boolean;
}>`
  position: relative;
  display: ${({ $isVisible }) => ($isVisible ? 'inline-block' : 'none')};

  ${({ $appearance }) =>
    $appearance === 'switch'
      ? css`
          width: 50px;
          height: 30px;
        `
      : css`
          flex: 0 0 30px;
          width: 30px;
          height: 30px;
        `}
`;

export const Box = styled.input`
  display: none;
`;

export const Knob = styled.span<{ $appearance: CheckboxAppearance }>`
  position: absolute;
  right: 0;
  bottom: 0;
  border-radius: ${({ $appearance }) =>
    $appearance === 'filledCheckIcon' ? '5px' : '34px'};
  cursor: pointer;

  ${({ $appearance }) =>
    $appearance === 'switch'
      ? css`
          top: 0;
          left: 0;
          background-color: rgba(120, 120, 128, 0.16);
          transition: background-color 0.3s;
        `
      : css`
          top: 5px;
          left: 5px;
          width: 20px;
          height: 20px;
          border: 1px solid ${({ theme }) => theme.palette.primary.border};
          background-color: ${({ theme }) => theme.palette.primary.background};
          transition: border-color 0.3s;
        `}

  &::before {
    content: '';
    position: absolute;
    border-radius: 50%;
    transition: transform 0.3s, background-color 0.3s, opacity 0.3s;
    ${({ $appearance }) => {
      switch ($appearance) {
        case 'switch':
          return css`
            height: 26px;
            width: 26px;
            left: 2px;
            bottom: 2px;
            background-color: ${({ theme }) => theme.palette.system.white};
            box-shadow: 0 1px 2px 1px rgba(0, 0, 0, 0.1);
          `;
        case 'filledCheckIcon':
          return css`
            opacity: 0;
            border-radius: 5px;
            width: 20px;
            height: 20px;
            top: -1px;
            left: -1px;
          `;
        default:
          return css`
            width: 12px;
            height: 12px;
            top: 3px;
            left: 3px;
          `;
      }
    }}
  }
`;

export const Wrapper = styled.div<{
  $hasBorderBottom?: boolean;
  $appearance: CheckboxAppearance;
  $size: CheckboxSize;
  $labelPosition: CheckboxLabelPosition;
  $color: CheckboxColor;
  $hasSmallGap?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${({ $hasSmallGap }) => ($hasSmallGap ? '5px' : '10px')};
  height: ${({ $size }) => `${getCheckboxWrapperHeight($size)}px`};

  ${({ $labelPosition }) =>
    $labelPosition === 'left'
      ? css`
          justify-content: space-between;
        `
      : css`
          flex-direction: row-reverse;
          justify-content: flex-end;
        `}

  ${({ $hasBorderBottom }) =>
    $hasBorderBottom &&
    css`
      &:not(:last-of-type) {
        border-bottom: 1px solid ${({ theme }) => theme.palette.primary.border};
      }
    `}

  ${({ $appearance }) =>
    $appearance === 'filledCheckIcon' &&
    css`
      width: 100%;
      height: 54px;
      padding: 0 15px;
      border-radius: 14px;
      border: 1px solid ${({ theme }) => theme.palette.primary.border};
      background-color: ${({ theme }) => theme.palette.quartenary.background};

      &,
      & label {
        cursor: pointer;
      }

      @media (min-width: 744px) {
        width: 260px;
      }

      @media (min-width: 950px) {
        width: 250px;
      }
    `}

  ${({ $appearance, $color }) => getCheckedKnobStyles($appearance, $color)}
`;

export const LabelBox = styled.label`
  display: flex;
  align-items: center;
  flex-grow: 1;
  gap: 10px;
  overflow: hidden;
`;

export const Label = styled(Typography.Body).attrs({
  $variant: '1',
})<{ $isChecked?: boolean }>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.palette.primary.labels};

  ${({ theme, $isChecked }) =>
    $isChecked &&
    css`
      background: ${() => theme.palette.gradient.main};
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    `}
`;

export const HelperText = styled(Typography.Body)<{
  $hasError?: boolean;
}>`
  margin: 5px 0;
  color: ${({ theme, $hasError }) =>
    $hasError ? theme.palette.system.red : theme.palette.primary.labels};
`;
